import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { githubApi } from '@/api/githubApi';
import { PageHeader } from '@/components/shared/PageHeader';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { GITHUB_ORG } from '@/config/github';
const SORT_MAP = {
    created_at: 'created',
    updated_at: 'updated',
    comments: 'comments',
};
export const IssueTrackerPage = () => {
    const navigate = useNavigate();
    const [issues, setIssues] = useState([]);
    const [repositories, setRepositories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [totalIssues, setTotalIssues] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const searchTimeoutRef = useRef(null);
    const [filters, setFilters] = useState({
        state: 'open',
        page: 1,
        perPage: 20,
        sort: 'updated',
        direction: 'desc',
    });
    useEffect(() => {
        loadRepositories();
    }, []);
    useEffect(() => {
        loadIssues();
    }, [filters]);
    const loadRepositories = async () => {
        try {
            const repos = await githubApi.getRepositories();
            setRepositories(repos);
        }
        catch (err) {
            console.error('Failed to load repositories:', err);
        }
    };
    const loadIssues = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await githubApi.getIssues(filters);
            setIssues(response.issues);
            setTotalIssues(response.total);
        }
        catch (err) {
            setError('Failed to load issues. Please try again.');
            console.error('Load issues error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleSort = (column) => {
        const newSort = SORT_MAP[column] || 'updated';
        setFilters((prev) => ({
            ...prev,
            sort: newSort,
            direction: prev.sort === newSort && prev.direction === 'desc' ? 'asc' : 'desc',
        }));
    };
    const handleStateFilter = (state) => {
        setFilters((prev) => ({
            ...prev,
            state,
            page: 1,
        }));
    };
    const handleRepositoryFilter = (repository) => {
        setFilters((prev) => ({
            ...prev,
            repository: repository || undefined,
            page: 1,
        }));
    };
    const handleSearch = useCallback((search) => {
        setSearchInput(search);
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        // Set new timeout for debounced search
        searchTimeoutRef.current = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                search: search || undefined,
                page: 1,
            }));
        }, 300);
    }, []);
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);
    const getRepositoryName = (issue) => {
        if (issue.repository) {
            return issue.repository.name;
        }
        if (issue.repository_url) {
            const parts = issue.repository_url.split('/');
            return parts[parts.length - 1] || 'Unknown';
        }
        return 'Unknown';
    };
    const columns = [
        {
            key: 'number',
            label: '#',
            render: (issue) => (_jsxs("span", { className: "font-mono text-sm text-gray-600", children: ["#", issue.number] })),
        },
        {
            key: 'title',
            label: 'Title',
            render: (issue) => (_jsxs("div", { className: "max-w-md", children: [_jsx("div", { className: "font-medium text-gray-900 truncate", children: issue.title }), _jsx("div", { className: "text-xs text-gray-500 mt-1", children: getRepositoryName(issue) })] })),
        },
        {
            key: 'state',
            label: 'State',
            render: (issue) => (_jsx(StatusBadge, { status: issue.state === 'open' ? 'open' : 'closed' })),
        },
        {
            key: 'labels',
            label: 'Labels',
            render: (issue) => (_jsxs("div", { className: "flex flex-wrap gap-1", children: [issue.labels.slice(0, 3).map((label) => (_jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium", style: {
                            backgroundColor: `#${label.color}20`,
                            color: `#${label.color}`,
                        }, children: label.name }, label.id))), issue.labels.length > 3 && (_jsxs("span", { className: "text-xs text-gray-500", children: ["+", issue.labels.length - 3] }))] })),
        },
        {
            key: 'assignees',
            label: 'Assignees',
            render: (issue) => (_jsxs("div", { className: "flex -space-x-2", children: [issue.assignees.slice(0, 3).map((assignee) => (_jsx("img", { src: assignee.avatar_url, alt: assignee.login, title: assignee.login, className: "w-6 h-6 rounded-full border-2 border-white" }, assignee.id))), issue.assignees.length > 3 && (_jsxs("div", { className: "w-6 h-6 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs", children: ["+", issue.assignees.length - 3] })), issue.assignees.length === 0 && (_jsx("span", { className: "text-sm text-gray-400", children: "Unassigned" }))] })),
        },
        {
            key: 'comments',
            label: 'Comments',
            sortable: true,
            render: (issue) => (_jsx("span", { className: "text-sm text-gray-600", children: issue.comments })),
        },
        {
            key: 'updated_at',
            label: 'Updated',
            sortable: true,
            render: (issue) => (_jsx("span", { className: "text-sm text-gray-600", children: new Date(issue.updated_at).toLocaleDateString() })),
        },
    ];
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "GitHub Issue Tracker", subtitle: `${totalIssues} total issues`, action: _jsx("button", { onClick: loadIssues, className: "btn-secondary", children: "\uD83D\uDD04 Refresh" }) }), error && (_jsx("div", { className: "mb-6", children: _jsx(ErrorAlert, { message: error, onDismiss: () => setError('') }) })), _jsx("div", { className: "card p-4 mb-6", children: _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-4", children: [_jsx("input", { type: "text", placeholder: "Search issues...", value: searchInput, onChange: (e) => handleSearch(e.target.value), className: "input" }), _jsxs("select", { onChange: (e) => handleStateFilter(e.target.value), value: filters.state, className: "input", children: [_jsx("option", { value: "open", children: "Open" }), _jsx("option", { value: "closed", children: "Closed" }), _jsx("option", { value: "all", children: "All" })] }), _jsxs("select", { onChange: (e) => handleRepositoryFilter(e.target.value), className: "input", children: [_jsx("option", { value: "", children: "All Repositories" }), repositories.map((repo) => (_jsx("option", { value: repo.full_name, children: repo.name }, repo.id)))] }), _jsxs("select", { onChange: (e) => setFilters((prev) => ({
                                ...prev,
                                sort: e.target.value
                            })), value: filters.sort, className: "input", children: [_jsx("option", { value: "updated", children: "Recently Updated" }), _jsx("option", { value: "created", children: "Recently Created" }), _jsx("option", { value: "comments", children: "Most Commented" })] })] }) }), _jsx(DataTable, { columns: columns, data: issues, isLoading: isLoading, emptyMessage: "No issues found", onSort: handleSort, sortColumn: filters.sort === 'updated' ? 'updated_at' : filters.sort === 'created' ? 'created_at' : 'comments', sortDirection: filters.direction, onRowClick: (issue) => {
                    const repoName = getRepositoryName(issue);
                    const owner = issue.repository?.owner.login || GITHUB_ORG;
                    navigate(`/issues/${owner}/${repoName}/${issue.number}`);
                }, actions: (issue) => (_jsx("a", { href: issue.html_url, target: "_blank", rel: "noopener noreferrer", onClick: (e) => e.stopPropagation(), className: "text-primary-600 hover:text-primary-900 font-medium", children: "View on GitHub \u2192" })) })] }));
};
